import React from 'react';
import { Loader2, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';

// Shared building blocks for the playbook editors (NDA markdown + TPRM
// structured form). Kept here so the page and both editors stay thin.

export const META_LABEL = {
  fontSize: 10,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  color: 'var(--text-tertiary)',
  marginBottom: 4,
};

// --- small immutable array helpers ---------------------------------------
const replaceAt = (arr, i, v) => arr.map((x, idx) => (idx === i ? v : x));
const removeAt = (arr, i) => arr.filter((_, idx) => idx !== i);

// --- shared chrome ---------------------------------------------------------

export function Badge({ isDefault, updatedAt }) {
  if (isDefault) {
    return <span className="chip gray" title="No custom version saved — the built-in default is in effect">Built-in default</span>;
  }
  return (
    <span className="chip green" title={updatedAt ? `Last saved ${new Date(updatedAt).toLocaleString()}` : undefined}>
      Custom · active
    </span>
  );
}

// The Reset / Save pair that lives in each editor card's header action slot.
export function SaveResetActions({ dirty, saving, onSave, onReset, saveDisabledReason }) {
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
      {dirty && (
        <button type="button" className="btn ghost sm" onClick={onReset} disabled={saving} title="Discard changes">
          <RotateCcw className="w-3.5 h-3.5" />
          Reset
        </button>
      )}
      <button
        type="button"
        className="btn primary sm"
        onClick={onSave}
        disabled={saving || !dirty || !!saveDisabledReason}
        title={saveDisabledReason || undefined}
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Save
      </button>
    </div>
  );
}

// Version label + display name inputs, with the default/active badge.
export function MetaRow({ version, name, onVersion, onName, badge }) {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={META_LABEL}>Version label</div>
        <input className="input" value={version} onChange={(e) => onVersion(e.target.value)} placeholder="e.g. 2026-06-22" />
      </div>
      <div style={{ flex: '2 1 300px', minWidth: 0 }}>
        <div style={META_LABEL}>Display name</div>
        <input className="input" value={name} onChange={(e) => onName(e.target.value)} placeholder="Display name" />
      </div>
      {badge && <div style={{ alignSelf: 'center' }}>{badge}</div>}
    </div>
  );
}

// A labelled section within a structured form.
export function FormSection({ title, hint, children, count }) {
  return (
    <section className="stack-sm" style={{ paddingTop: 4 }}>
      <div>
        <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
          {typeof count === 'number' && (
            <span className="muted" style={{ fontSize: 11.5 }}>{count} {count === 1 ? 'item' : 'items'}</span>
          )}
        </div>
        {hint && <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>{hint}</div>}
      </div>
      {children}
    </section>
  );
}

function RemoveBtn({ onRemove }) {
  return (
    <button type="button" className="icon-btn xs" onClick={onRemove} title="Remove" aria-label="Remove" style={{ color: 'var(--text-critical)', flexShrink: 0 }}>
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}

function AddButton({ onClick, label = 'Add' }) {
  return (
    <div>
      <button type="button" className="btn ghost sm" onClick={onClick}>
        <Plus className="w-3.5 h-3.5" />
        {label}
      </button>
    </div>
  );
}

// A reorderable list of free-text lines, each in its own block. Good for
// sentence-style policy rules and document lists.
export function LineListEditor({ value = [], onChange, placeholder = 'Add an item…', multiline = true }) {
  const items = Array.isArray(value) ? value : [];
  return (
    <div className="stack-sm">
      {items.length === 0 && <div className="muted" style={{ fontSize: 12 }}>None yet — add one below.</div>}
      {items.map((item, i) => (
        <div key={i} className="pb-item">
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <textarea
              className="pb-bare"
              value={item}
              onChange={(e) => onChange(replaceAt(items, i, e.target.value))}
              rows={multiline ? 2 : 1}
              placeholder={placeholder}
              style={{ resize: multiline ? 'vertical' : 'none', minHeight: multiline ? 38 : 22 }}
            />
            <RemoveBtn onRemove={() => onChange(removeAt(items, i))} />
          </div>
        </div>
      ))}
      <AddButton onClick={() => onChange([...items, ''])} />
    </div>
  );
}

export const SEVERITIES = ['low', 'medium', 'high'];
const SEVERITY_DOT = { low: 'var(--color-green-500)', medium: 'var(--color-yellow-500)', high: 'var(--color-red-500)' };

// A visible 3-way severity picker. The selected option fills with its semantic
// color (green/yellow/red); the others stay muted outlines, so which one is
// active is unmistakable.
export function SeverityToggle({ value = 'medium', onChange }) {
  return (
    <div role="group" aria-label="How serious" style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {SEVERITIES.map((s) => {
        const selected = value === s;
        return (
          <button
            key={s}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(s)}
            className={`sev-btn ${s}${selected ? ' selected' : ''}`}
          >
            <span className="sev-dot" style={{ background: selected ? SEVERITY_DOT[s] : 'var(--text-tertiary)' }} />
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        );
      })}
    </div>
  );
}

// A reorderable list of plain-language rules. Each row is just a sentence the
// editor writes; an optional severity picker sits beside it. The machine-facing
// `id` is hidden and generated on save (see normalizeTprmCriteria), so editors
// never see internal keys.
export function RuleRowsEditor({ value = [], onChange, withSeverity = false, placeholder = 'Describe the rule in plain language…' }) {
  const rows = Array.isArray(value) ? value : [];
  const setRow = (i, patch) => onChange(replaceAt(rows, i, { ...rows[i], ...patch }));
  const blank = withSeverity ? { description: '', severity: 'medium' } : { description: '' };
  const controls = (i) => <RemoveBtn onRemove={() => onChange(removeAt(rows, i))} />;
  return (
    <div className="stack-sm">
      {rows.length === 0 && <div className="muted" style={{ fontSize: 12 }}>None yet — add one below.</div>}
      {rows.map((row, i) => (
        <div key={i} className="pb-item">
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <textarea
              className="pb-bare"
              value={row.description || ''}
              onChange={(e) => setRow(i, { description: e.target.value })}
              rows={2}
              placeholder={placeholder}
              style={{ resize: 'vertical', minHeight: 38 }}
            />
            {!withSeverity && controls(i)}
          </div>
          {withSeverity && (
            <div className="pb-item-footer">
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 11.5 }}>How serious?</span>
                <SeverityToggle value={row.severity || 'medium'} onChange={(s) => setRow(i, { severity: s })} />
              </div>
              {controls(i)}
            </div>
          )}
        </div>
      ))}
      <AddButton onClick={() => onChange([...rows, blank])} />
    </div>
  );
}

// A grouped checklist of known request fields. `value` is the array of selected
// field keys; toggling preserves the registry's order and keeps any unknown
// keys already present.
export function FieldChecklist({ groups, value = [], onChange, humanize = (k) => k }) {
  const selected = new Set(value);
  const knownKeys = groups.flatMap((g) => g.fields.map((f) => f[0]));
  const knownSet = new Set(knownKeys);
  const extras = (value || []).filter((k) => !knownSet.has(k));

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    const ordered = knownKeys.filter((k) => next.has(k));
    const keptExtras = [...next].filter((k) => !knownSet.has(k));
    onChange([...ordered, ...keptExtras]);
  };

  const renderRow = (key, label) => (
    <label key={key} className="pb-check" title={label}>
      <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} />
      <span>{label}</span>
    </label>
  );

  const grid = (children) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '1px 12px' }}>
      {children}
    </div>
  );

  return (
    <div className="stack-md">
      {groups.map((g) => (
        <div key={g.title}>
          <div style={{ ...META_LABEL, marginBottom: 6 }}>{g.title}</div>
          {grid(g.fields.map(([key, label]) => renderRow(key, label)))}
        </div>
      ))}
      {extras.length > 0 && (
        <div>
          <div style={{ ...META_LABEL, marginBottom: 6 }}>Other</div>
          {grid(extras.map((key) => renderRow(key, humanize(key))))}
        </div>
      )}
    </div>
  );
}

// --- save-time normalization ---------------------------------------------
const slugify = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 48) || 'item';

// Fill machine ids for rows that lack one (derived from the description),
// dedupe them, and drop rows the editor left blank.
function withIds(rows) {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r && (r.description || '').trim())
    .map((r) => {
      let id = (r.id || '').trim() || slugify(r.description);
      const base = id;
      let n = 2;
      while (seen.has(id)) { id = `${base}-${n++}`; }
      seen.add(id);
      return { ...r, id, description: r.description.trim() };
    });
}

const cleanList = (arr) => (Array.isArray(arr) ? arr : [])
  .map((s) => (typeof s === 'string' ? s.trim() : s))
  .filter((s) => (typeof s === 'string' ? s : true) && s !== '');

const cleanTiers = (obj) => ({
  ...(obj || {}),
  low: cleanList(obj?.low),
  medium: cleanList(obj?.medium),
  high: cleanList(obj?.high),
});

// Turn the form's working draft into the shape the backend stores: generate
// hidden ids, drop empty rows/lines, trim. Unknown keys pass through untouched.
export function normalizeTprmCriteria(criteria) {
  const out = { ...(criteria || {}) };
  if ('hardGates' in out) out.hardGates = withIds(out.hardGates);
  if ('legalChecks' in out) out.legalChecks = withIds(out.legalChecks);
  if ('securityChecks' in out) out.securityChecks = withIds(out.securityChecks);
  if (Array.isArray(out.requiredFields)) out.requiredFields = cleanList(out.requiredFields);
  if (out.riskRules) out.riskRules = cleanTiers(out.riskRules);
  if (out.recommendedDocuments) out.recommendedDocuments = cleanTiers(out.recommendedDocuments);
  return out;
}
